import { Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import './CapacityMatrix.less';
import { OperatorPerPlatformItem } from 'types/capacityManagement.types';
import {
  CapacityMatrixRecord,
  resourcesOf,
  SizeResources,
  SizeSummary,
} from '../capacitySummary';

function compact({ memoryRequest, memoryLimit, cpu }: SizeResources) {
  return `${memoryRequest}/${memoryLimit} · ${cpu}`;
}

// Long cluster codes share the provider and country prefix; the column header shows the rest.
function shortCode(clusterCode: string) {
  return clusterCode.replace(/^[a-z]+-[a-z]+-/, '');
}

function CapacityMatrix({
  summaries,
  operators,
  loading,
  canEdit,
  onCellClick,
}: Readonly<{
  summaries: SizeSummary[];
  operators: OperatorPerPlatformItem[];
  loading: boolean;
  canEdit: boolean;
  onCellClick: (record: CapacityMatrixRecord, clusterCode: string) => void;
}>) {
  const differingByOperator = new Map<string, number>();
  summaries.forEach(summary =>
    summary.operators.forEach(({ operator, differs }) => {
      if (differs) {
        differingByOperator.set(
          operator.clusterCode,
          (differingByOperator.get(operator.clusterCode) ?? 0) + 1,
        );
      }
    }),
  );

  const columns: ColumnsType<SizeSummary> = [
    {
      title: 'Size',
      key: 'size',
      fixed: 'left',
      width: 190,
      render: (_, summary) => (
        <span className="capacity-matrix__size">
          <span className="capacity-matrix__size-title">{summary.size}</span>
          <span className="capacity-matrix__muted">
            {summary.isCustom ? 'Custom' : 'Standard'}
          </span>
        </span>
      ),
    },
    {
      title: 'Baseline',
      key: 'baseline',
      fixed: 'left',
      width: 130,
      className: 'capacity-matrix__baseline',
      render: (_, { baseline }) => (
        <span className="capacity-matrix__mono">
          {baseline ? compact(baseline) : '—'}
        </span>
      ),
    },
    ...operators.map((operator, index) => {
      const differing = differingByOperator.get(operator.clusterCode) ?? 0;
      return {
        title: (
          <span
            className="capacity-matrix__header"
            title={operator.clusterCode}
          >
            <span className="capacity-matrix__mono">
              {shortCode(operator.clusterCode)}
            </span>
            <span
              className={`capacity-matrix__count${
                differing ? ' capacity-matrix__count--differs' : ''
              }`}
            >
              {differing ? `${differing} differ` : 'all match'}
            </span>
          </span>
        ),
        key: operator.clusterCode,
        width: 116,
        align: 'center' as const,
        render: (_, summary: SizeSummary) => {
          const { cell, differs } = summary.operators[index];
          if (!cell) {
            return (
              <button
                type="button"
                className="capacity-matrix__cell capacity-matrix__cell--missing"
                disabled={!canEdit}
                aria-label={`Add ${summary.size} on ${operator.clusterCode}`}
                onClick={() =>
                  onCellClick(summary.record, operator.clusterCode)
                }
              >
                +
              </button>
            );
          }
          const value = compact(resourcesOf(cell));
          return (
            <button
              type="button"
              className={`capacity-matrix__cell capacity-matrix__cell--${
                differs ? 'differs' : 'match'
              }`}
              aria-label={`${summary.size} on ${operator.clusterCode}: ${value}${
                differs ? ', differs from baseline' : ', matches baseline'
              }`}
              title={value}
              onClick={() => onCellClick(summary.record, operator.clusterCode)}
            >
              {differs ? value : '✓'}
            </button>
          );
        },
      };
    }),
  ];

  return (
    <div className="capacity-matrix">
      <ul className="capacity-matrix__legend" aria-label="Legend">
        <li>
          <span className="capacity-matrix__swatch capacity-matrix__cell--match">
            ✓
          </span>
          Matches baseline
        </li>
        <li>
          <span className="capacity-matrix__swatch capacity-matrix__cell--differs" />
          Differs (memory request/limit GB · vCPU)
        </li>
        <li>
          <span className="capacity-matrix__swatch capacity-matrix__cell--missing" />
          Not offered, click to add
        </li>
      </ul>
      <Table<SizeSummary>
        className="capacity-matrix__table"
        rowKey="size"
        size="small"
        bordered
        dataSource={summaries}
        columns={columns}
        loading={loading}
        pagination={false}
        sticky
        scroll={{ x: 320 + operators.length * 116 }}
        locale={{ emptyText: 'No sizes match this filter' }}
      />
    </div>
  );
}

export default CapacityMatrix;
